import React, { useState, useMemo } from 'react';
import { AgTableCard, Input, Button } from "../UI";
import SaveTaskModal from './Modal/SaveTaskModal';
import { CSVLink } from "react-csv";
import { formatDate } from '../../utils/converter';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { permission } from '../../config/permission';
import { useUserContext } from '../../contexts/UserContext';
import { Search, Download, ChevronDown } from 'lucide-react';

const ListActivity = ({ data, loading, error }) => {
    const [gridApi, setGridApi] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [downloadDropdown, setDownloadDropdown] = useState(false);
    const { content } = useContentfulContext();
    const { checkPermission, userData } = useUserContext();

    // Define the columns for ag-Grid
    const columns = [
        {
            headerName: content.LIST_ACTIVITIES_CONTENT.activities_table_headers.activity_id,
            field: "id",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            headerName: content.LIST_ACTIVITIES_CONTENT.activities_table_headers.activity_name,
            field: "name",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 200,
        },
        {
            headerName: content.LIST_ACTIVITIES_CONTENT.activities_table_headers.activity_type,
            field: "type",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            headerName: content.LIST_ACTIVITIES_CONTENT.activities_table_headers.activity_state,
            field: "state",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            headerName: content.LIST_ACTIVITIES_CONTENT.activities_table_headers.activity_priority,
            field: "priority",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 150,
        },
        {
            headerName: "Workspace",
            field: "workspace",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 200,
            cellRenderer: ({ value }) => userData?.workspacesMap?.[value] || value || "NA",
            filterValueGetter: (params) => {
                const val = userData?.workspacesMap?.[params.data.workspace] || params.data.workspace || "NA";
                return val ? val.toString().toLowerCase() : '';
            }
        },
        {
            headerName: content.LIST_ACTIVITIES_CONTENT.activities_table_headers.modified_at,
            field: "modifiedAt",
            sortable: true,
            filter: true,
            resizable: true,
            minWidth: 200,
            cellRenderer: ({ value }) => formatDate(value),
        },
    ];

    // Function to handle search input
    const handleSearch = (e) => {
        const val = typeof e === 'string' ? e : e?.target?.value || '';
        setSearchText(val);
        gridApi?.setQuickFilter(val);
    };

    // Get filtered data for CSV download
    const getFilteredData = () => {
        const filteredData = [];
        gridApi?.forEachNodeAfterFilterAndSort((node) => filteredData.push(node.data));
        return filteredData;
    };

    // Handle grid ready event
    const onGridReady = (params) => {
        setGridApi(params.api);
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: window.innerWidth < 1024 ? "fitCellContents" : "fitGridWidth",
            defaultMinWidth: 150,
            columnLimits: columns.map((col) => ({
                colId: col.field,
                minWidth: col.minWidth || 100,
            })),
        };
    }, []);

    return (
        <div className="w-full">
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                        {content.LIST_ACTIVITIES_CONTENT.title}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">{content.LIST_ACTIVITIES_CONTENT.description}</p>
                </div>
                <div className="relative flex items-center gap-3">
                    {checkPermission(permission.FUNCTIONS_ONE_OFF_TASK_CREATE) && data?.length > 0 && (
                        <SaveTaskModal feature_id={content.products.adobe_target.id} />
                    )}
                    {checkPermission(permission.FUNCTIONS_ONE_OFF_TASK_DOWNLOAD) && data?.length > 0 && (
                        <div className="relative">
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => setDownloadDropdown(!downloadDropdown)}
                                icon={<Download size={14} />}
                            >
                                <span>Download as CSV</span>
                                <ChevronDown size={14} className="ml-1" />
                            </Button>
                            {downloadDropdown && (
                                <div className="z-10 bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-slate-200 w-44 absolute top-full right-0 mt-1">
                                    <ul className="py-2 text-sm text-gray-700">
                                        <li onClick={() => setDownloadDropdown(false)}>
                                            <CSVLink
                                                data={data}
                                                filename={`target-activities-${new Date().getTime()}`}
                                                className="block px-4 py-2 hover:bg-slate-50"
                                            >
                                                All
                                            </CSVLink>
                                        </li>
                                        <li onClick={() => setDownloadDropdown(false)}>
                                            <CSVLink
                                                data={getFilteredData()}
                                                filename={`target-activities-${new Date().getTime()}`}
                                                className="block px-4 py-2 hover:bg-slate-50"
                                            >
                                                Filtered
                                            </CSVLink>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Filter and Table Container */}
            <AgTableCard
                header={
                    <div className="w-full sm:w-80">
                        <Input
                            prefixIcon={<Search size={15} />}
                            placeholder="Search activity name"
                            value={searchText}
                            onChange={handleSearch}
                            clearable
                        />
                    </div>
                }
                tableId="list-activities-table"
                loading={loading}
                emptyMessage={content.LIST_ACTIVITIES_CONTENT.no_data_found_msg}
                rowData={data || []}
                columnDefs={columns}
                defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                }}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
            >
                {error ? (
                    <h3 className="text-center py-6 text-rose-600 font-medium">{error}</h3>
                ) : null}
            </AgTableCard>
        </div>
    );
};

export default ListActivity;
